import {
  ICustomAlarmItem,
  ILegend,
  ILegendItem,
  Legend,
} from "../../Legend/Legend";
import React, { useMemo } from 'react';
import { useChartVariables, ChartLegendType } from '../hooks/Chart.context';

interface ChartLegendProps {
  hasLegendCategoryFilter?: boolean;
  showDescription?: boolean;
  thereshold?: ILegendItem;
  customAlarms?: ICustomAlarmItem[];
}

type buildLegendDataProps = {
  chartLegends: ChartLegendType[];
  shouldFilter?: boolean;
  legendCategory?: string;
  selectedChartVariables: string[];
  showDescription?: boolean;
};

export const buildLegendData = ({
  chartLegends,
  shouldFilter,
  legendCategory,
  selectedChartVariables,
  showDescription = true,
}: buildLegendDataProps): ILegendItem[] => {
  const legendData = chartLegends.map((item) => ({
    ...item,
    label: item.label,
    description: showDescription ? item.description : undefined,
    enabled: selectedChartVariables.indexOf(item.reference) !== -1,
  }));

  const orderedLegendData = legendData.sort((a, b) => a.label.localeCompare(b.label));

  return shouldFilter
    ? orderedLegendData.filter(
        (legend) => legendCategory && legend.category.includes(legendCategory),
      )
    : orderedLegendData;
};

const ChartLegend: React.FC<ChartLegendProps & Omit<ILegend, 'data'>> = ({
  hasLegendCategoryFilter,
  showDescription = true,
  thereshold,
  ...legendProps
}) => {
  const { chartLegends, legendCategory, selectedChartVariables, handleLegendClick } =
    useChartVariables();
  const shouldFilter = Boolean(hasLegendCategoryFilter) && legendCategory !== 'ALL';
  const legendsData: ILegendItem[] = useMemo(() => {
    return buildLegendData({
      chartLegends,
      shouldFilter,
      legendCategory,
      selectedChartVariables,
      showDescription,
    });
  }, [chartLegends, selectedChartVariables, legendCategory, hasLegendCategoryFilter]);

  return (
    <Legend
      data={thereshold ? [...legendsData, thereshold] : legendsData}
      onClick={(item) => handleLegendClick(item.reference)}
      orientation="fill"
      hasAlarm
      {...legendProps}
    />
  );
};

export default ChartLegend;
